import { DateTime } from 'luxon'

import type { CivilDate, CivilDateRange } from './types'

/**
 * The Past / Today / Upcoming selection the List and Details views filter by.
 * Kept here rather than in the web package because the calendar anchor is the
 * one piece of that filter's meaning both platforms need.
 */
export type SchedulePeriod = 'PAST' | 'TODAY' | 'UPCOMING'

const CIVIL_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * `/games/dates` answers with ATOM instants (`2026-03-25T20:15:00-07:00`), one
 * per day that has games. The calendar only ever asks "which month", so the
 * civil date in the instant is the whole answer — and taking it by prefix keeps
 * the zone the API already applied instead of re-resolving it against the
 * browser's, which is what would slide an 8:15pm game into the next day.
 */
export const toCivilGameDate = (value: unknown): CivilDate | null => {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return null
  }

  const head = value.slice(0, 10)
  if (CIVIL_DATE.test(head)) {
    return head
  }

  //  Anything else — a bare timestamp, a `/`-separated date — still gets one
  //  honest attempt before it is dropped.
  const parsed = DateTime.fromISO(value)
  return parsed.isValid ? parsed.toFormat('yyyy-MM-dd') : null
}

/**
 * The civil date a calendar should OPEN on for a period, given the days that
 * actually have games.
 *
 * The calendar used to open on the current real-world month whatever the
 * filters said, so a team whose season ended in March landed on an empty August
 * with the period control greyed out — every bounded calendar layout drives its
 * own window, so there was no control left to steer with either. This is the
 * missing step: read the period the way List and Details read it, and point the
 * calendar at the nearest month that has something in it.
 *
 * - `PAST` — the most RECENT day with games before today. Past is browsed
 *   backwards from now, so the newest past month is the one being asked for.
 * - `UPCOMING` — the SOONEST day with games from today on, for the mirror
 *   reason.
 * - `TODAY` — today, always. The period is a single day; if it has no games the
 *   honest answer is still the month containing it, not a jump somewhere else.
 *
 * Returns `null` when the period has no games at all, which the caller reads as
 * "keep the default" rather than as an error — there is no better month to
 * offer, and moving the user somewhere arbitrary would be worse than not moving
 * them.
 */
export const calendarAnchorForPeriod = (
  gameDates: readonly unknown[],
  period: SchedulePeriod,
  today: CivilDate
): CivilDate | null => {
  if (period === 'TODAY') {
    return today
  }

  const dates = gameDates
    .map(toCivilGameDate)
    .filter((date): date is CivilDate => date !== null)

  if (dates.length === 0) {
    return null
  }

  //  Civil dates are fixed-width and zero-padded, so lexical order IS date
  //  order — no parsing needed to find the nearest one.
  if (period === 'PAST') {
    const past = dates.filter((date) => date < today)
    return past.length === 0 ? null : past.reduce((a, b) => (a > b ? a : b))
  }

  const upcoming = dates.filter((date) => date >= today)
  return upcoming.length === 0
    ? null
    : upcoming.reduce((a, b) => (a < b ? a : b))
}

/**
 * Where the calendar should open when the admin has picked an explicit DATE
 * RANGE, which outranks Past / Today / Upcoming everywhere else in these views.
 *
 * No day index needed, and none asked for: the range is already the answer to
 * "where are the games I want". Today wins while it falls inside the range —
 * moving someone off the current month when the range contains it would be the
 * same disorientation in the other direction — and otherwise the calendar opens
 * on the range's first day.
 *
 * A one-ended range (the picker allows it) anchors on the end it has.
 */
export const calendarAnchorForRange = (
  range: CivilDateRange | null,
  today: CivilDate
): CivilDate | null => {
  if (range === null) {
    return null
  }

  const { start, endExclusive } = range
  const startsBeforeToday = start === null || start <= today
  const endsAfterToday = endExclusive === null || today < endExclusive

  if (startsBeforeToday && endsAfterToday) {
    return today
  }

  if (start !== null && today < start) {
    return start
  }

  //  The range is entirely behind us: its LAST day, which is the day before the
  //  exclusive end.
  return endExclusive === null
    ? null
    : DateTime.fromISO(endExclusive, { zone: 'UTC' })
        .minus({ days: 1 })
        .toFormat('yyyy-MM-dd')
}
